'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalToggleButton, NormalButton } from '../../theme/components';
import useShiberseLandAuction from '../../../hooks/useShiberseLandAuction';
import { apiServer, Events, EventsText } from '../../../constants/map';
import axios from 'axios';
import { useAccount } from 'wagmi';
import { formatFromBalance, shortenAddress, shortenDouble } from '../../../utils';
import { getLandName, getLandImage } from '../../../utils/mapHelper';
import Close from '../../../public/images/x.svg';
import ShiberseLoader from '../../../components/Loader/loader';
import MintModal, { MintMultiModal } from '../MintModal/MintModal';
import { useMapContext } from '../../../contexts/map-context';
import Image from 'next/image';
import { GET_LAND_INFO_BY_ID } from '../../../apollo/queries';
import { graphEndpoint } from '../../../helper/subgraphs';
import { ethers } from 'ethers';
import { puppyNetChainId, shibariumChainId } from '../../../constants';
import { useConnectModal } from '@rainbow-me/rainbowkit';

const LandDetailPanel = styled.div<{ $show: string; $expanded: string }>`
  display: ${({ $show }) => ($show === 'true' ? 'block' : 'none')};
  opacity: ${({ $show }) => ($show === 'true' ? 1 : 0)};
  background: #130f0fcc;
  border-radius: 8px;
  width: 350px;
  transition: all 1s ease;
  position: absolute;
  top: 12rem;
  right: 2rem;
  padding: 1.5rem;
  height: ${({ $expanded }) => ($expanded === 'true' ? 'auto' : '78.7px')};
  transition: all 0.5s;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 90%;
    right: 2%;
  }

  @media (max-width: 480px) {
    width: 90%;
    right: 2%;
  }
`;

const LandInfo = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 2rem;
`;

const LandImage = styled.div`
  margin-right: 1rem;
  width: 50px;
`;

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 75px;
  margin-bottom: 1rem;
`;

const LandName = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #ffd59d;
`;

const LandType = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
`;

const LandCoordinates = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  img {
    width: 14px;
    margin-right: 0.5rem;
  }
`;

const OpenType = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 10px;
  line-height: 15px;
  color: #ffffff;
`;

const CloseIcon = styled.div`
  color: #fff;
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
  svg {
    width: 20px;
  }
`;

const CloseColor = styled(Close)`
  path {
    stroke: '#fff';
  }
`;
const HeaderControls = styled.div`
  position: absolute;
  right: 0.6rem;
  top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExpandIcon = styled.div`
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    opacity: 0.6;
  }
`;

export const LandDetail = () => {
  const [showMintModal, setShowMintModal] = useState(false); // State for mint modal
  const [mintType, setMintType] = useState('shib'); // State for mint type
  const [currentLandInfo, setCurrentLandInfo] = useState({}) as any;
  const [currentSelectedMultipleLandInfo, setCurrentSelectedMultipleLandInfo] = useState([]) as any;
  const [isLoading, setIsLoading] = useState(true);
  const { openConnectModal } = useConnectModal();
  const { address: account, chain } = useAccount();
  const [expanded, setExpanded] = useState(true);

  const { currentStage, fetchLandShibPrice, fetchLandPrice } = useShiberseLandAuction({});

  const { state, dispatch } = useMapContext();

  const toggleMintModal = () => {
    setShowMintModal(prev => !prev);
  };

  const setSelectedInfo = (newLandInfo: object) => {
    dispatch({ type: 'SET_SELECTED_LAND_INFO', payload: { newLandInfo } });
  };

  const canShowButton = useCallback(() => {
    if (!account) return false;
    if (Object.keys(currentLandInfo).length === 0) return false;
    const hasUnclaimedWinner =
      currentLandInfo?.bidWinner &&
      currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
      !currentLandInfo.isMinted;
    if (hasUnclaimedWinner) {
      return currentLandInfo.bidWinner?.toLowerCase() === account.toLowerCase();
    }
    return currentStage === Events['Public'];
  }, [account, currentStage, currentLandInfo]);

  const checkDistrict = (x: number, y: number) => {
    if (x >= -96 && x < 0) {
      if (y <= 99 && y > 0) {
        return 'Defense';
      } else {
        return 'Technology';
      }
    } else {
      if (x > 0 && x <= 96) {
        if (y < 0 && y >= -99) {
          return 'Currency';
        } else {
          return 'Growth';
        }
      }
    }
    return '';
  };
  const shouldFetchPrice = (selected: any) => {
    return (
      selected?.tierName &&
      selected?.tierName !== 'hub' &&
      selected?.tierName !== 'road' &&
      !selected?.reserved
    );
  };

  const fetchAndSetPrices = async (selected: any) => {
    const shibPrice = await fetchLandShibPrice({
      x: selected.coordinates.x,
      y: selected.coordinates.y,
    });
    const ethPrice = await fetchLandPrice({ x: selected.coordinates.x, y: selected.coordinates.y });
    if (shibPrice && shibPrice._hex && shibPrice._isBigNumber) {
      selected.shibPrice = ethers.formatEther(shibPrice._hex);
      selected.bigNumbShibPrice = shibPrice;
    }
    if (ethPrice && ethPrice._hex && ethPrice._isBigNumber) {
      selected.ethPrice = ethers.formatEther(ethPrice._hex);
      selected.bigNumbEthPrice = ethPrice;
    }
    return selected;
  };
  const fetchLandDetails = async (selected: any) => {
    try {
      const GET_LAND_INFO_BY_ID_ENDPOINT = graphEndpoint(true);
      const GET_LAND_INFO_BY_ID_DATA: any = await axios.post(GET_LAND_INFO_BY_ID_ENDPOINT, {
        query: GET_LAND_INFO_BY_ID(selected.id),
      });
      const landInfo = GET_LAND_INFO_BY_ID_DATA.data.data.landInfoById;
      if (!landInfo || Object.keys(landInfo).length === 0) {
        console.error(`Failed to fetch land data by id`);
        return selected;
      }
      const newInfo = { ...landInfo };
      if (
        Object.keys(newInfo).length &&
        newInfo.owner == '0x0000000000000000000000000000000000000000'
      ) {
        if (shouldFetchPrice(newInfo)) {
          return await fetchAndSetPrices(newInfo);
        }
      }
      return newInfo;
    } catch (error) {
      console.log('Error fetching land details by id:', error);
      return selected; // Return the item unchanged if there's an error
    }
  };

  useEffect(() => {
    const updateSelectedInfo = async () => {
      setIsLoading(true); // Set loading state at the start of the operation
      try {
        if (state.selectedLandInfo.length === 1) {
          const newInfo = await fetchLandDetails(state.selectedLandInfo[0]);
          setCurrentLandInfo(newInfo);
          return;
        } else if (state.selectedLandInfo.length > 1) {
          const updatedInfo = await Promise.all(state.selectedLandInfo.map(fetchLandDetails));
          setCurrentSelectedMultipleLandInfo(updatedInfo);
          return;
        }
      } catch (error) {
        console.error('Error updating selected info:', error);
      } finally {
        setIsLoading(false); // Set loading state to false after all operations complete
      }
    };

    updateSelectedInfo();
  }, [state.selectedLandInfo]);

  const handleClose = () => {
    setCurrentSelectedMultipleLandInfo([]);
    setSelectedInfo([]);
    setCurrentLandInfo({});
    setShowMintModal(false);
    setIsLoading(false);
  };

  const hideDetail = (info: any) =>
    info?.tierName === 'road' || info?.tierName === 'hub' || info?.reserved;

  const isMultiplePossible = useCallback(() => {
    if (!account) return false;
    if (currentStage !== Events['Public']) return false;
    if (currentSelectedMultipleLandInfo.some((item: any) => item.isMinted === true)) return false;
    if (
      currentSelectedMultipleLandInfo.some(
        (item: any) =>
          item?.bidWinner &&
          item.bidWinner !== '0x0000000000000000000000000000000000000000' &&
          !item.isMinted &&
          item.bidWinner?.toLowerCase() !== account?.toLowerCase()
      )
    )
      return false;
    return true;
  }, [account, currentStage, currentSelectedMultipleLandInfo]);

  return (
    <LandDetailPanel
      $show={state.selectedLandInfo.length > 0 ? 'true' : 'false'}
      $expanded={expanded ? 'true' : 'false'}
    >
      <HeaderControls>
        <ExpandIcon onClick={() => setExpanded(prev => !prev)}>
          {expanded ? (
            <Image
              src={'/images/chevron-down-black.png'}
              alt="Collapse"
              className="mt-1"
              width={24}
              height={24}
            />
          ) : (
            <Image
              src={'/images/chevron-up-black.png'}
              alt="Expand"
              className="mt-1"
              width={24}
              height={24}
            />
          )}
        </ExpandIcon>
        <CloseIcon onClick={handleClose}>
          <CloseColor />
        </CloseIcon>
      </HeaderControls>

      {isLoading ? (
        <div className="flex justify-center sm:py-24 md:py-24 lg:py-24 ">
          <ShiberseLoader size="50px" />
        </div>
      ) : Object.keys(currentLandInfo).length > 0 && state.selectedLandInfo.length == 1 ? (
        <>
          <LandInfo className="flex cursor-default">
            <LandImage>
              {getLandImage(state.selectedLandInfo[0]?.tierName) && (
                <Image
                  src={getLandImage(state.selectedLandInfo[0]?.tierName)}
                  alt="pic"
                  width={50}
                  height={50}
                />
              )}
            </LandImage>

            <DetailInfo>
              <LandName>
                {state.selectedLandInfo[0]?.isRoad
                  ? 'Road'
                  : state.selectedLandInfo[0]?.tierName === 'hub'
                    ? 'HUB'
                    : 'Land'}
              </LandName>
              <LandType className="text-red-500">
                {state.selectedLandInfo[0]?.reserved ? 'Reserved' : ''}
              </LandType>
              <LandType>
                {getLandName(state.selectedLandInfo[0]?.tierName, state.selectedLandInfo[0])}
              </LandType>
              <LandType>
                District:{' '}
                {checkDistrict(
                  Number(state.selectedLandInfo[0]?.coordinates?.x),
                  Number(state.selectedLandInfo[0]?.coordinates?.y)
                )}
              </LandType>
              {state.selectedLandInfo[0].owner === '0x0000000000000000000000000000000000000000' &&
              !state.selectedLandInfo[0].reserved &&
              !state.selectedLandInfo[0].isMinted &&
              !(
                currentLandInfo.bidWinner &&
                currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                !currentLandInfo.isMinted
              ) &&
              currentLandInfo.shibPrice ? (
                <LandType>
                  Price in SHIB: {`${Number(currentLandInfo.shibPrice).toFixed(2)}`}
                </LandType>
              ) : null}
              {state.selectedLandInfo[0].owner === '0x0000000000000000000000000000000000000000' &&
              !state.selectedLandInfo[0].reserved &&
              !(
                currentLandInfo.bidWinner &&
                currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                !currentLandInfo.isMinted
              ) &&
              currentLandInfo.ethPrice ? (
                <LandType>Price in ETH: {`${currentLandInfo.ethPrice}`}</LandType>
              ) : null}
            </DetailInfo>
          </LandInfo>

          <LandCoordinates className="mb-2 flex items-center text-white">
            <Image src={'/images/map/location.svg'} alt="Location Icon" width={18} height={18} />
            X: {state.selectedLandInfo[0]?.coordinates?.x} Y:{' '}
            {state.selectedLandInfo[0]?.coordinates?.y}
          </LandCoordinates>

          {state.selectedLandInfo[0]?.tierName === 'hub' ? (
            <div className="mt-4 text-center">
              <a href="https://zfrmz.com/oCjfhnhUrelKmFbSU7yS" target="_blank" rel="noreferrer">
                <NormalButton className={`px-8 font-bold text-white`}>
                  For Leasing Contact us
                </NormalButton>
              </a>
            </div>
          ) : null}

          {hideDetail(state.selectedLandInfo[0]) ? (
            ''
          ) : (
            <>
              <LandType>
                Owner:
                {currentLandInfo.bidWinner &&
                currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                !currentLandInfo.isMinted ? (
                  <a
                    className="ml-1 font-normal"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://etherscan.io/address/${currentLandInfo?.bidWinner}`}
                  >
                    {shortenAddress(currentLandInfo?.bidWinner, 8)}
                  </a>
                ) : currentLandInfo?.owner &&
                  currentLandInfo.owner !== '0x0000000000000000000000000000000000000000' ? (
                  <a
                    className="ml-1 font-normal"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://etherscan.io/address/${currentLandInfo?.owner}`}
                  >
                    {shortenAddress(currentLandInfo?.owner, 8)}
                  </a>
                ) : (
                  ' None'
                )}
              </LandType>

              {/* <OpenType className="mb-4">{EventsText[currentStage]}</OpenType> */}

              {/* {Number(currentLandInfo?.owner) ? (
                                <div className="text-center">
                                    <a
                                        href={`https://opensea.io/assets/0xEfAEd650f1a94801806BB110019d9B0dc79531A8/${currentLandInfo.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <NormalButton className={`px-6 m-0 font-bold text-white`}>View on Opensea</NormalButton>
                                    </a>
                                </div>
                            ) : null} */}
              {!account &&
                !currentLandInfo.isMinted &&
                !(
                  currentLandInfo?.bidWinner &&
                  currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                  !currentLandInfo.isMinted
                ) && (
                  <>
                    <br />
                    <NormalButton
                      className={`w-full px-3 font-bold`}
                      onClick={() => {
                        handleClose();
                        openConnectModal?.();
                      }}
                    >
                      Connect Wallet to mint
                    </NormalButton>
                  </>
                )}
              {canShowButton() ? (
                <div className="mt-6 flex flex-col space-y-4 text-center font-quicksand text-white">
                  {currentLandInfo.bidWinner &&
                  currentLandInfo.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                  currentLandInfo.bidWinner.toLowerCase() == account?.toLowerCase() &&
                  !currentLandInfo.isMinted ? (
                    <div className="flex flex-col space-y-4">
                      <NormalButton
                        className={`mb-2 px-6 font-bold ${Number(currentLandInfo?.owner) ? 'hidden' : ''}`}
                        onClick={() => {
                          toggleMintModal();
                          setMintType('eth');
                        }}
                      >
                        {chain?.id === puppyNetChainId || chain?.id == shibariumChainId
                          ? 'Claim with BONE'
                          : 'Claim with ETH'}
                      </NormalButton>
                    </div>
                  ) : currentStage === Events['Public'] &&
                    currentLandInfo.shibPrice &&
                    currentLandInfo.ethPrice ? (
                    <div className="flex flex-col space-y-4">
                      <NormalButton
                        className={`mb-2 px-6 font-bold ${Number(currentLandInfo?.owner) ? 'hidden' : ''}`}
                        onClick={() => {
                          toggleMintModal();
                          if (chain?.id === puppyNetChainId || chain?.id == shibariumChainId) {
                            setMintType('weth');
                          } else {
                            setMintType('eth');
                          }
                        }}
                      >
                        Mint With ETH
                      </NormalButton>
                      <NormalButton
                        className={`px-5 font-bold ${Number(currentLandInfo?.owner) ? 'hidden' : ''}`}
                        onClick={() => {
                          toggleMintModal();
                          setMintType('shib');
                        }}
                      >
                        Mint With Shib
                      </NormalButton>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {showMintModal && (
                <MintModal
                  isOpen={showMintModal}
                  onDismiss={toggleMintModal}
                  landInfo={currentLandInfo}
                  handleCloseAction={handleClose}
                  mintType={mintType}
                />
              )}
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col space-y-4 text-center font-quicksand text-sm text-white">
          <div className="mx-auto flex max-w-md flex-col rounded-lg shadow-lg">
            <div className="mt-1 flex space-x-4">
              <div>
                <h2 className="mb-4 text-xl">{state.selectedLandInfo.length} Land Selected</h2>
              </div>
              <div>
                <button className="mb-4 border-b pb-1 hover:opacity-80" onClick={handleClose}>
                  Clear Selection
                </button>
              </div>
            </div>

            <div className={`max-h-80 cursor-default overflow-y-auto text-sm`}>
              {currentSelectedMultipleLandInfo.length > 1 &&
                currentSelectedMultipleLandInfo.length === state.selectedLandInfo.length &&
                currentSelectedMultipleLandInfo.map((land: any, index: number) => (
                  <div key={index} className="mb-4 flex space-x-4 border-b border-gray-300 pb-4">
                    <div className="flex flex-col items-center justify-center">
                      <LandImage>
                        <Image
                          src={getLandImage(land?.tierName) || '/images/land.png'}
                          alt="pic"
                          width={50}
                          height={50}
                        />
                      </LandImage>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold">Land</h3>
                      <p>
                        <strong>Zone:</strong> {getLandName(land.tierName, land)}
                      </p>
                      <p>
                        <strong>District:</strong>{' '}
                        {checkDistrict(Number(land.coordinates.x), Number(land.coordinates.y))}
                      </p>
                      <p>
                        <strong>Location:</strong> X: {land.coordinates.x} Y: {land.coordinates.y}
                      </p>
                      {currentSelectedMultipleLandInfo[index].owner ===
                        '0x0000000000000000000000000000000000000000' &&
                      !currentSelectedMultipleLandInfo[index].reserved &&
                      !(
                        currentSelectedMultipleLandInfo[index].bidWinner &&
                        currentSelectedMultipleLandInfo[index].bidWinner !==
                          '0x0000000000000000000000000000000000000000' &&
                        !currentSelectedMultipleLandInfo[index].isMinted
                      ) &&
                      currentSelectedMultipleLandInfo[index].shibPrice ? (
                        <p>
                          <strong>SHIB PRICE:</strong>
                          {Number(currentSelectedMultipleLandInfo[index].shibPrice).toFixed(2)}
                        </p>
                      ) : null}

                      {currentSelectedMultipleLandInfo[index].ethPrice &&
                      currentSelectedMultipleLandInfo[index].owner ===
                        '0x0000000000000000000000000000000000000000' &&
                      !currentSelectedMultipleLandInfo[index].reserved &&
                      !(
                        currentSelectedMultipleLandInfo[index].bidWinner &&
                        currentSelectedMultipleLandInfo[index].bidWinner !==
                          '0x0000000000000000000000000000000000000000' &&
                        !currentSelectedMultipleLandInfo[index].isMinted
                      ) ? (
                        <p>
                          <strong>ETH PRICE:</strong>{' '}
                          {currentSelectedMultipleLandInfo[index].ethPrice}
                        </p>
                      ) : null}
                      <p>
                        <strong>Owner:</strong>
                        {land.owner &&
                        land.bidWinner !== '0x0000000000000000000000000000000000000000' &&
                        !land.isMinted
                          ? shortenAddress(land.bidWinner, 2)
                          : land.owner !== '0x0000000000000000000000000000000000000000'
                            ? shortenAddress(land.owner, 2)
                            : 'None'}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          {currentSelectedMultipleLandInfo.length > 1 &&
            currentSelectedMultipleLandInfo.length === state.selectedLandInfo.length && (
              <div className="flex flex-col space-y-4">
                {!account ? (
                  <NormalButton
                    className={`w-full px-3 font-bold`}
                    onClick={() => {
                      handleClose();
                      openConnectModal?.();
                    }}
                  >
                    Connect Wallet to mint
                  </NormalButton>
                ) : (
                  <>
                    <NormalButton
                      className={`m-0 mb-2 px-3 font-bold`}
                      disabled={!isMultiplePossible()}
                      onClick={() => {
                        toggleMintModal();
                        if (chain?.id === puppyNetChainId || chain?.id == shibariumChainId) {
                          setMintType('weth');
                        } else {
                          setMintType('eth');
                        }
                      }}
                    >
                      Submit Multimint With ETH({state.selectedLandInfo.length})
                    </NormalButton>

                    <NormalButton
                      className={`m-0 px-3 font-bold`}
                      disabled={!isMultiplePossible()}
                      onClick={() => {
                        toggleMintModal();
                        setMintType('shib');
                      }}
                    >
                      Submit Multimint With Shib({state.selectedLandInfo.length})
                    </NormalButton>
                  </>
                )}

                {showMintModal && (
                  <MintMultiModal
                    isOpen={showMintModal}
                    onDismiss={toggleMintModal}
                    selectedInfo={state.selectedLandInfo}
                    handleCloseAction={handleClose}
                    mintType={mintType}
                  />
                )}
              </div>
            )}
        </div>
      )}
    </LandDetailPanel>
  );
};

export default LandDetail;
